import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import withSize from 'react-sizeme';

import Sizes from '../definitions/Sizes';
import MessagePropType from '../definitions/MessagePropType';
import useForm from '../hooks/useForm';
import useFieldset from '../hooks/useFieldset';
import WizardSizes from '../definitions/WizardSizes';
import { QUESTION_ANIMATION_DURATION_MS } from '../definitions/AnimationDurations';
import renderMessage from '../functions/renderMessage';
import { ALIGNMENT_OPTIONS, LOCATION_OPTIONS } from './Tooltip';
import OpacityContainer from './OpacityContainer';
import TooltipAnchor from './TooltipAnchor';
import Paragraph from './Paragraph';
import InputCompleteButton from './InputCompleteButton';
import AnimatedContainer, { ANIMATION_TYPES, AnimationTypePropType } from './AnimatedContainer';
import {
    BOTTOM as TOOLTIP_BOTTOM_LOCATION,
    END as TOOLTIP_END_ALIGNMENT,
} from './Tooltip';


function QuestionContainer({
    labelFor,
    labelMessage,

    tooltipId,
    tooltipMessage,
    tooltipLocation = TOOLTIP_BOTTOM_LOCATION,  // by default, tool tip is below
    tooltipAlignment = TOOLTIP_END_ALIGNMENT,   // by default, nubbin is at the end/right of the tooltip

    quicktipMessage,

    isVisible = true,
    animationType = ANIMATION_TYPES.FADE,

    isInvalid = false,

    isOptional = false,
    showOptionalText = true,

    className,

    children,
    size,

    onCompleteButtonClick,
    showCompleteButtonEnterText = false,
    forceCompleteButtonVisibility = false,

    isSideBySide,
    shouldRemoveMargin,

    ...otherProps
}) {
    const intl = useIntl();
    const form = useForm();
    const fieldset = useFieldset();

    const containerElement = useRef(null);

    const isAutoFocusEnabled = fieldset ? fieldset.isAutoFocusEnabled : !!form?.isAutoFocusEnabled;
    const isCondensed = !isAutoFocusEnabled;


    function getTooltipWidth() {
        // If we're side by side, tooltip width is 85% of one of the columns
        if (isSideBySide && size?.width >= ELEMENT_SIZES.COLUMN_WIDTH * 2 + ELEMENT_SIZES.GAP_WIDTH) {
            return ((size.width - ELEMENT_SIZES.GAP_WIDTH) / 2) * 0.85;
        }

        // Width is 80% of our container size or 260 as a default
        return size?.width
            ? Math.max(size.width * 0.85, 260)
            : 260;
    }

    return (
        <AnimatedContainer
            isVisible={isVisible}
            type={animationType}
            duration={QUESTION_ANIMATION_DURATION_MS}
            alwaysRenderChildMarkup
            {...otherProps}
        >
            <OpacityContainer isViewBased={isAutoFocusEnabled}>
                <Container
                    ref={containerElement}
                    isInvalid={isInvalid}
                    isCondensed={isCondensed}
                    isSideBySide={isSideBySide}
                    isAutoFocusEnabled={isAutoFocusEnabled}
                    isButtonVisible={!!onCompleteButtonClick}
                    forceCompleteButtonVisibility={forceCompleteButtonVisibility}
                    className={classNames(className, {
                        'rh-mt-1 rh-mb-3': isCondensed && !shouldRemoveMargin,
                        'rh-my-2': !isCondensed && !shouldRemoveMargin,
                    })}
                    shouldRemoveMargin={shouldRemoveMargin}
                >
                    <div className="label-container rh-position-relative rh-mb-0_5">
                        <div className="label-and-tooltip rh-position-relative rh-display-flex rh-flex-wrap">
                            {/* Need to preserve renderMessage for Question component usage */}
                            <label
                                className={classNames('label', 'rh-display-block', 'rh-m-0',
                                    'rh-flex-grow-1', 'rh-flex-shrink-0', 'rh-align-items-center',
                                    isCondensed ? 'rh-title-xs' : 'rh-title-m',
                                    isInvalid ? 'rh-fg-strawberry-dark' : 'rh-fg-blackberry',
                                    {
                                        'rh-mb-0_5': !isSideBySide && isCondensed,
                                    },
                                )}
                                htmlFor={labelFor}
                            >
                                {typeof labelMessage === 'string' ? labelMessage : renderMessage(labelMessage, intl).children}
                            </label>
                            <If condition={tooltipMessage}>
                                <TooltipAnchor
                                    id={tooltipId}
                                    width={`${getTooltipWidth()}px`}
                                    tooltipLocation={tooltipLocation}   // relates to nubbinLocation in TooltipAnchor
                                    nubbinAlignment={tooltipAlignment}
                                    message={tooltipMessage}
                                    data-name={tooltipId}
                                />
                            </If>
                        </div>

                        <If condition={isOptional && showOptionalText}>
                            <Paragraph
                                className="optional-label rh-fg-stone-darkest"
                                size="medium"
                                message={(
                                    <FormattedMessage
                                        id="base-ui.question.optional-label"
                                        defaultMessage="(Optional)"
                                    />
                                )}
                            />
                        </If>

                        <If condition={quicktipMessage}>
                            <Paragraph
                                className="quick-tip"
                                message={quicktipMessage}
                                size={isCondensed ? 'small' : 'medium'}
                            />
                        </If>
                    </div>

                    <div className="child-container rh-position-relative">
                        {children}

                        <If condition={isAutoFocusEnabled && !!onCompleteButtonClick}>
                            <InputCompleteButton
                                className="input-complete-button rh-position-absolute"
                                onClick={onCompleteButtonClick}
                                showEnterText={showCompleteButtonEnterText}
                                data-name={`${labelFor}-complete`}
                            />
                        </If>
                    </div>
                </Container>
            </OpacityContainer>
        </AnimatedContainer>
    );
}

QuestionContainer.propTypes = {
    labelFor: PropTypes.string.isRequired,
    labelMessage: MessagePropType.isRequired,

    tooltipId: PropTypes.string,
    tooltipMessage: MessagePropType,
    tooltipWidth: PropTypes.number,
    tooltipLocation: PropTypes.oneOf(LOCATION_OPTIONS),
    tooltipAlignment: PropTypes.oneOf(ALIGNMENT_OPTIONS),

    quicktipMessage: MessagePropType,

    isVisible: PropTypes.bool,
    animationType: AnimationTypePropType,

    isInvalid: PropTypes.bool,

    isOptional: PropTypes.bool,
    showOptionalText: PropTypes.bool,

    className: PropTypes.string,
    children: PropTypes.node.isRequired,

    onCompleteButtonClick: PropTypes.func,
    showCompleteButtonEnterText: PropTypes.bool,
    forceCompleteButtonVisibility: PropTypes.bool,

    isSideBySide: PropTypes.bool,
    shouldRemoveMargin: PropTypes.bool,
    size: PropTypes.object.isRequired,
};


const ELEMENT_SIZES = {
    COLUMN_WIDTH: WizardSizes.QUESTION_COLUMN_MIN_WIDTH,
    GAP_WIDTH: 56,
};


const Container = styled.div`
    ${props => !props.isCondensed && `
        padding: ${WizardSizes.AUTO_FOCUS_QUESTION_TOP} 0 0;
    `}

    ${props => props.isSideBySide && `
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(${ELEMENT_SIZES.COLUMN_WIDTH}px, 1fr));
        grid-column-gap: ${WizardSizes.QUESTION_COLUMN_GAP};
    `}

    > .label-container {
        height: fit-content;

        > .label-and-tooltip {
            > .label {
                flex-basis: calc(100% - ${Sizes.SPACING.FOUR});
                transition: color 300ms;
            }
        }

        > .quick-tip {
            ${props => props.isCondensed && `
                margin: ${Sizes.SPACING.HALF} 0 0;
            `}
        }

        > .optional-label {
            font-style: italic;

            ${props => props.isCondensed && `
                margin: 0;
            `}
        }

        > *:last-child:not(.label-and-tooltip) {
            margin-bottom: 0;
        }
    }

    > .child-container {
        /* Padding at the bottom is needed so the spacing between each
           question is consistent with space taken up by the complete button */
        ${props => props.isAutoFocusEnabled && `
            padding-bottom: ${Sizes.SPACING.THREE};
        `}

        > .input-complete-button {
            bottom: -${Sizes.SPACING.HALF};
            left: 0;

            opacity: 0;

            /* Safari iOS does not focus buttons on click, since this
                button is visible only on focus-within we have to ensure
                its visible since the button click will 1) Blur the question
                (causing our focus-within styles to not apply) and then 2) fire the
                button's onClick event. The only way to get the onClick to fire is
                to ensure the button is not hidden. */
            /* visibility: hidden; */
            transition: opacity 300ms;
        }

        ${props => props.isAutoFocusEnabled && props.isButtonVisible && `
            &:focus-within > .input-complete-button {
                opacity: 1;
                visibility: visible;
            }
        `}

        ${props => props.isAutoFocusEnabled && props.forceCompleteButtonVisibility && `
            > .input-complete-button {
                opacity: 1;
                visibility: visible;
            }
        `}
    }
`;

export default withSize({ noPlaceholder: true })(QuestionContainer);
